import React from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormDropdown,
    FormFields,
    FormNumberTextField,
    FormTextField,
    Stack
} from "sprint-common-web";

import { EncodeSpecificationMpeg1ListingItem } from "../../EncodeSpecificationListingItem";
import { useEditEncodeSpecificationForm } from "./useEditEncodeSpecificationForm";

export interface EditEncodeSpecificationMpeg1FormProps {
    item: EncodeSpecificationMpeg1ListingItem;
    onCancel(): void;
    onSubmit(): void;
}

export interface EditEncodeSpecificationMpeg1FormInputs {
    name: string;
    framerate23_976: string;
    framerate24: string;
    framerate25: string;
    framerate29_97: string;
    framerate30: string;
    framerate50: string;
    framerate59_94: string;
    framerate60: string;
    framerateOther: string;
    aspectType: string;
    targetAspectRatioId: string;
    slug?: number;
}

export const EditEncodeSpecificationMpeg1Form: React.FC<EditEncodeSpecificationMpeg1FormProps> = (
    props: EditEncodeSpecificationMpeg1FormProps
) => {
    const { item, onCancel, onSubmit } = props;

    const { errors, formState, control, reset, handleSubmit } = useForm<EditEncodeSpecificationMpeg1FormInputs>({
        defaultValues: {
            name: item.name,
            framerate23_976: item.details.frameratePresets["23.976"].id,
            framerate24: item.details.frameratePresets["24"].id,
            framerate25: item.details.frameratePresets["29.97"].id,
            framerate29_97: item.details.frameratePresets["29.97"].id,
            framerate30: item.details.frameratePresets["30"].id,
            framerate50: item.details.frameratePresets["50"].id,
            framerate59_94: item.details.frameratePresets["59.94"]?.id,
            framerate60: item.details.frameratePresets["60"].id,
            framerateOther: item.details.frameratePresets["OTHER"]?.id,
            slug: item.details.slug,
            aspectType: item.details.aspectType.toString(),
            targetAspectRatioId: item.targetAspectRatio.id
        },
        criteriaMode: "all"
    });

    const {
        manzanitaPresetOptions,
        validateNameUnique,
        editEncodeSpecMpeg1,
        aspectTypesOptions,
        aspectRatioOptions
    } = useEditEncodeSpecificationForm();

    const handleEditEncodeSpecification = async (inputs: EditEncodeSpecificationMpeg1FormInputs) => {
        await editEncodeSpecMpeg1(item.id, inputs);
        onSubmit();
        reset();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleEditEncodeSpecification)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormTextField
                            label="Name"
                            control={control}
                            name="name"
                            errors={errors}
                            rules={{
                                required: "Name is required field",
                                validate: (name) => validateNameUnique(name, item.id)
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 23.976"}
                            name={"framerate23_976"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 23.976"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 24"}
                            name={"framerate24"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 24"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 25"}
                            name={"framerate25"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 25"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 29.97"}
                            name={"framerate29_97"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 29.97"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 30"}
                            name={"framerate30"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 30"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 50"}
                            name={"framerate50"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 50"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 59.94"}
                            name={"framerate59_94"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 59.94"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate 60"}
                            name={"framerate60"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate 60"
                            }}
                        />
                        <FormDropdown
                            label={"Framerate Other"}
                            name={"framerateOther"}
                            control={control}
                            errors={errors}
                            options={manzanitaPresetOptions}
                            rules={{
                                required: "Select preset for framerate Other"
                            }}
                        />
                        <FormDropdown
                            label={"Aspect type"}
                            name={"aspectType"}
                            control={control}
                            errors={errors}
                            options={aspectTypesOptions}
                            rules={{
                                required: "Select aspect type"
                            }}
                        />
                        <FormDropdown
                            label={"Target aspect ratio"}
                            name={"targetAspectRatioId"}
                            control={control}
                            errors={errors}
                            options={aspectRatioOptions}
                            rules={{
                                required: "Select target aspect ratio"
                            }}
                        />
                        <FormNumberTextField label="Slug" control={control} name="slug" errors={errors} />
                    </FormFields>
                    <FormActions
                        isLoading={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};
